import React, { useState, useEffect } from 'react';
import sendgridService from '../../services/sendgrid';
import { useAuth } from "context/Auth";

const DomainSettings: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [domains, setDomains] = useState<any[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [domain, setDomain] = useState('');
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [isDialogSubmitting, setIsDialogSubmitting] = useState(false);
  const [validatingDomains, setValidatingDomains] = useState<{ [key: string]: boolean }>({});
  const [deletingDomains, setDeletingDomains] = useState<{ [key: string]: boolean }>({});
  const { user, refreshUser } = useAuth();

  const fetchDomains = async () => {
    if (!user?.currentOrganization?.sendgridConfig?.user_id) return;

    setLoading(true);
    setError(null);

    try {
      const domains = await sendgridService.getSubuserDomains();
      setDomains(domains);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch domains');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDomain = async () => {
    if (!domain.trim() || !user?.currentOrganization?.sendgridConfig?.user_id) return;

    setIsDialogSubmitting(true);
    setDialogError(null);

    try {
      await sendgridService.authenticateDomain(user.currentOrganization.sendgridConfig.user_id, domain);
      await refreshUser();
      await fetchDomains();
      setOpenDialog(false);
      setDomain('');
    } catch (err: any) {
      setDialogError(err.response?.data?.message || 'Failed to add domain');
    } finally {
      setIsDialogSubmitting(false);
    }
  };

  const handleValidateDomain = async (domainId: string) => {
    setValidatingDomains(prev => ({ ...prev, [domainId]: true }));
    try {
      const response = await sendgridService.validateDomain(domainId);
      setDomains((prev: any) => prev.map(domain =>
        String(domain.id) === String(domainId) ? { ...domain, valid: response.valid } : domain
      ));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to validate domain');
    } finally {
      setValidatingDomains(prev => ({ ...prev, [domainId]: false }));
    }
  };

  const handleDeleteDomain = async (domainId: string) => {
    setDeletingDomains(prev => ({ ...prev, [domainId]: true }));
    try {
      await sendgridService.deleteDomain(domainId);
      await refreshUser();
      await fetchDomains();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete domain');
    } finally {
      setDeletingDomains(prev => ({ ...prev, [domainId]: false }));
    }
  };

  useEffect(() => {
    if (user?.currentOrganization?.sendgridConfig?.user_id) {
      fetchDomains();
    }
  }, [user?.currentOrganization?.sendgridConfig?.user_id]);

  if (!user?.currentOrganization?.sendgridConfig?.user_id) {
    return (
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-700">
              You need to enable email in the General Settings tab before configuring domains.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4">
          {error}
        </div>
      )}

      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Email Domains</h2>
          <button
            onClick={() => setOpenDialog(true)}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Add Domain
          </button>
        </div>

        <p className="text-gray-600 mb-6">
          Configure domains for sending emails through SendGrid. Properly configured domains improve deliverability and reduce the chance of your emails being marked as spam.
        </p>

        {loading ? (
          <div className="flex justify-center py-8">
            <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        ) : domains.length === 0 ? (
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  No domains configured yet. Add a domain to improve email deliverability.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {domains.map((domain) => (
              <div key={domain.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">{domain.domain}</h3>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleValidateDomain(domain.id)}
                        disabled={validatingDomains[domain.id]}
                        className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {validatingDomains[domain.id] ? (
                          <div className="flex items-center">
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Validating...
                          </div>
                        ) : 'Validate DNS'}
                      </button>
                      <button
                        onClick={() => handleDeleteDomain(domain.id)}
                        disabled={deletingDomains[domain.id]}
                        className="px-3 py-1 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {deletingDomains[domain.id] ? (
                          <div className="flex items-center">
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-red-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Deleting...
                          </div>
                        ) : 'Delete'}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center mb-4">
                    <span className="text-sm text-gray-600 mr-2">
                      Status:
                    </span>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${domain.valid
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                        }`}
                    >
                      {domain.valid ? '✓ Validated' : '⚠️ Not Validated'}
                    </span>
                  </div>

                  {domain.dns && (
                    <>
                      <div className="border-t border-gray-200 my-4"></div>
                      <h4 className="text-sm font-semibold mb-3">DNS Records</h4>

                      <div className="space-y-4">
                        {Object.entries(domain.dns).map(([key, record]: [string, any]) => (
                          <div key={key} className="mb-4">
                            <h5 className="text-sm font-semibold mb-2">
                              {key.toUpperCase()} - {record?.type?.toUpperCase()} Record
                            </h5>
                            <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                  <p className="text-xs text-gray-500">Host:</p>
                                  <p className="text-sm font-mono">{record.host}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500">Points to:</p>
                                  <p className="text-sm font-mono">{record.data}</p>
                                </div>
                                <div className="sm:col-span-2">
                                  <div className="flex items-center">
                                    <p className="text-xs text-gray-500 mr-1">Status:</p>
                                    <span
                                      className={`text-sm font-medium ${record.valid ? 'text-green-600' : 'text-red-600'
                                        }`}
                                    >
                                      {record.valid ? '✓ Valid' : '⚠️ Invalid'}
                                    </span>
                                  </div>
                                  {record.reason && (
                                    <p className="text-sm text-red-600 mt-1">{record.reason}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal for adding domain */}
      {openDialog && (
        <div className="fixed inset-0 z-10 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setOpenDialog(false)}></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                  Add Domain
                </h3>

                {dialogError && (
                  <div className="mt-3 bg-red-50 border border-red-200 text-red-800 rounded-md p-3 text-sm">
                    {dialogError}
                  </div>
                )}

                <div className="mt-4">
                  <label htmlFor="domain" className="block text-sm font-medium text-gray-700">
                    Domain Name
                  </label>
                  <input
                    type="text"
                    name="domain"
                    id="domain"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="example.com"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Enter the domain name you would like to authenticate for sending emails.
                    You will need to configure DNS records to verify domain ownership.
                  </p>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white sm:ml-3 sm:w-auto sm:text-sm ${isDialogSubmitting || !domain.trim()
                    ? 'bg-blue-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                    }`}
                  onClick={handleAddDomain}
                  disabled={isDialogSubmitting || !domain.trim()}
                >
                  {isDialogSubmitting ? (
                    <div className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Adding...
                    </div>
                  ) : 'Add Domain'}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setOpenDialog(false)}
                  disabled={isDialogSubmitting}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DomainSettings; 